import {inject, Injectable} from '@angular/core';
import {StoredPicture, UploadState} from "../../core/models/stored-picture";
import {
  SyncAction,
  SyncContext,
  SyncResult,
  confirmedServerSteps,
  decideSync,
  isConfirmed
} from "../../core/models/analysis-sync";
import {PictureStoreService} from "./picture-store.service";
import {UploadQueueService} from "./upload-queue.service";

@Injectable({
  providedIn: 'root'
})
export class AnalysisSyncService {

  private store = inject(PictureStoreService);
  private queue = inject(UploadQueueService);

  async sync(context: Omit<SyncContext, 'pictures'>): Promise<SyncResult> {
    const pictures = await this.store.all();
    const action = decideSync({...context, pictures});
    const confirmedSteps = confirmedServerSteps(context.analysis);

    switch (action) {
      case SyncAction.PURGE:
      case SyncAction.ABORT_IDENTIFICATION:
        await this.store.clear();
        await this.queue.refreshProgress();
        break;
      case SyncAction.RESUME:
        await this.consolidate(pictures, confirmedSteps);
        await this.queue.resume();
        break;
      case SyncAction.REUSE:
        await this.store.retagAnalysis(context.analysis.id!);
        await this.queue.resume();
        break;
      case SyncAction.NONE:
        break;
    }

    return {action, confirmedSteps};
  }

  private async consolidate(pictures: StoredPicture[], confirmedSteps: string[]): Promise<void> {
    for (const picture of pictures) {
      if (isConfirmed(picture, confirmedSteps)) {
        await this.store.markUploaded(picture.key, picture.uploadedAt);
      } else {
        await this.store.patch(picture.key, {
          uploadState: UploadState.PENDING,
          uploadedAt: undefined,
          attempts: 0,
          lastError: undefined
        });
      }
    }
  }
}
